/**
 * WHAT THE POLICY MADE OF THIS OFFER — on the awaiting-review card itself.
 *
 * ── WHY THIS IS HERE AND NOT ON A SCREEN OF ITS OWN ──
 *
 * It was a screen of its own: a table of decisions beside a queue of the same
 * offers, so an operator saw a row with no idea why it was waiting and had to
 * go and find it in a second list. One list, enriched, beats two lists that
 * agree. So the queue card now carries its own decision and the standalone
 * table is gone — the drill-down (the full rule trace) and the log (history,
 * including offers the queue never shows) survive, because neither is a second
 * copy of this queue.
 *
 * ── THE THREE THINGS THIS SAYS, IN THE ORDER THEY MATTER ──
 *
 * 1. THE AI's CONCERN. The thing the queue has never had and no rule can
 *    reach: the reviewer reads the booking's own free text — the comment, the
 *    seat counts — so a child seat asked for in a sentence is visible here and
 *    nowhere else on this page. It leads when there is one.
 * 2. WHY IT IS WAITING, in the reconciled vocabulary (`heldReasonCopy`, whose
 *    keys are the API's own `SYNC_HELD_REASONS`). Never a paraphrase: a
 *    table-sized second spelling of a closed enum is a second thing to
 *    reconcile every time the policy gains a rule.
 * 3. THE WAY THROUGH to the whole trace, for the question this strip cannot
 *    answer — which rule, measured against what.
 *
 * ── AND THE ONE IT MUST NOT SAY ──
 *
 * A row the policy has not decided is NOT a row it cleared. Both exist on this
 * queue at once, because an offer that has only just arrived has no decision
 * yet — its own pass has been asked for and has not written one — and an
 * offer whose trigger never fired has none until the daily safety-net sweep
 * picks it up. So an undecided row gets a grey badge
 * and a sentence saying nothing has been checked — never silence, which reads
 * as "nothing wrong", and never a verdict's colouring.
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  concernList, decisionHeadline, heldReasonCopy, reviewPresentation,
} from '../decisionPresentation';
import { QUEUE_DECISION_ABSENCE, QUEUE_DECISION_STATE } from '../queueDecisions';

/**
 * The reviewer's verdict, as a badge beside the policy's own.
 *
 * Shown for ALL THREE verdicts, including `unavailable`: "the reviewer did not
 * run on this one" is a fact an operator should be able to read off the row
 * rather than infer from its absence. The kinds are a separate line below the
 * reason — see the note there.
 */
function ReviewBadge({ review }) {
  const verdict = reviewPresentation(review);
  if (!verdict) return null;

  return (
    <Badge
      bg={verdict.badge.bg}
      text={verdict.badge.text}
      className="sync-status"
      title={verdict.blurb}
    >
      <i className="fa fa-robot me-1" aria-hidden="true" />
      {verdict.badge.label}
    </Badge>
  );
}

function QueueDecision({ state, decision, reference }) {
  const traceHref = `/partner-sync/auto-approval/${encodeURIComponent(reference)}`;

  if (state !== QUEUE_DECISION_STATE.DECIDED || !decision) {
    const absence = QUEUE_DECISION_ABSENCE[state];
    if (!absence) return null;

    return (
      <div className="sync-decision sync-decision--absent">
        <div className="sync-decision-head">
          <Badge bg={absence.badge.bg} text={absence.badge.text} className="sync-status">
            {absence.badge.label}
          </Badge>
          {/* The log is still worth offering on an UNKNOWN row: the drill-down
              asks the endpoint again by reference, so it can answer the
              question this card could not. An UNSCORED row has nothing to
              drill into, and is not offered a link to an empty screen. */}
          {state === QUEUE_DECISION_STATE.UNKNOWN && (
            <Link className="sync-decision-trace" to={traceHref}>Look it up</Link>
          )}
        </div>
        <p className="sync-decision-reason mb-0">{absence.copy}</p>
      </div>
    );
  }

  const headline = decisionHeadline(decision);
  const concerns = concernList(decision.review);

  return (
    <div className="sync-decision">
      <div className="sync-decision-head">
        <Badge
          bg={headline.badge.bg}
          text={headline.badge.text}
          className="sync-status"
        >
          {headline.badge.label}
        </Badge>
        <ReviewBadge review={decision.review} />
        <Link className="sync-decision-trace" to={traceHref}>
          Full rule trace
          <i className="fa fa-arrow-right ms-1" aria-hidden="true" />
        </Link>
      </div>

      {/* The reconciled sentence, in full. Clamped by CSS rather than cut in
          JS so the whole thing is still selectable and on hover.
          EVERY held row gets one, including `auto_approval_disabled` — whose
          copy says out loud that it is a setting and not a fault in the
          booking, which is exactly what an operator needs from a grey "Not
          switched on" badge. `decisionHeadline().showHeldReason` suppresses
          it there, but only because the drill-down ALSO prints the
          policy-stood-down sentence and would otherwise say it twice; this
          card prints no such sentence, so suppressing it here would just lose
          the explanation. */}
      {decision.decision === 'held' && (
        <p
          className="sync-decision-reason mb-0"
          title={heldReasonCopy(decision.held_reason)}
        >
          {heldReasonCopy(decision.held_reason)}
        </p>
      )}

      {/* AFTER the reason, not beside the badge — `ai_concern`'s copy ends
          "read the concern BELOW and decide", and on a card where the kinds
          sat up in the badge row that sentence pointed the wrong way. The
          evidence is the reviewer quoting the booking, and it is on the
          pill's title: a card stays a card, and the quote is one hover from
          the words that sent you looking for it. */}
      {concerns.length > 0 && (
        <div className="sync-decision-concerns">
          {concerns.map((concern) => (
            <span
              className="sync-decision-concern"
              key={concern.kind}
              title={concern.evidence || undefined}
            >
              {concern.kind}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default QueueDecision;
